export const BUNDLE_SIZE = 3;
export const GRID_SIZE = 6;
export const SOURCE_PROPERTY = 'power-step';

/**
 * @typedef {Object} CartLine
 * @property {string} key - Shopify line item key
 * @property {number} variantId
 * @property {number} productId
 * @property {number} quantity
 * @property {string|null} strength - Strength option value, e.g. "+2"
 * @property {string|null} collection - Glasses collection metafield value
 * @property {Object} [properties] - Line item properties
 * @property {string} [title]
 * @property {string} [image]
 */

/**
 * @typedef {Object} Product
 * @property {number} productId
 * @property {number} variantId - Variant at the active strength
 * @property {string} title
 * @property {string} image
 * @property {string} [model] - Glasses model metafield value
 * @property {boolean} [available] - Variant at the active strength is in stock
 */

/** @typedef {{ variantId: number, productId: number }} AddedLine */
/** @typedef {{ strength: string|null, variantId: number }} StagedPair */
/**
 * @typedef {Object<number, StagedPair[]>} Staged - Staged pairs per product id, oldest first.
 * Each pair holds the strength it was staged at, so a later strength change
 * leaves it where the customer put it.
 */

function quantityOf(lines, predicate = () => true) {
  return lines.filter(predicate).reduce((total, line) => total + line.quantity, 0);
}

/**
 * The cart line for the pair just added. Matches the variant only, so the
 * strength shown is always the one the customer chose.
 * @param {CartLine[]} cartLines
 * @param {AddedLine} addedLine
 * @returns {CartLine|null}
 */
export function findAddedLine(cartLines, addedLine) {
  return cartLines.find((line) => line.variantId === addedLine.variantId) || null;
}

function linesInCollection(cartLines, addedLine) {
  const added = findAddedLine(cartLines, addedLine);
  if (!added || !added.collection) return [];
  return cartLines.filter((line) => line.collection === added.collection);
}

function stagedPairs(staged, productId) {
  return staged[productId] || [];
}

function stagedQuantity(staged, productId) {
  return stagedPairs(staged, productId).length;
}

/**
 * Stages one more pair of a product at the strength shown on its card.
 * @param {Staged} staged
 * @param {Product} product
 * @param {string|null} strength
 * @returns {Staged}
 */
export function stagePair(staged, product, strength) {
  const pairs = [...stagedPairs(staged, product.productId), { strength, variantId: product.variantId }];
  return { ...staged, [product.productId]: pairs };
}

/**
 * Removes the pair staged last for a product, so minus undoes the latest plus.
 * @param {Staged} staged
 * @param {number} productId
 * @returns {Staged}
 */
export function unstagePair(staged, productId) {
  const pairs = stagedPairs(staged, productId).slice(0, -1);
  const next = { ...staged };
  if (pairs.length > 0) {
    next[productId] = pairs;
  } else {
    delete next[productId];
  }
  return next;
}

// Plus has no cap: the bundle is three pairs, but a customer can build a larger
// order. Minus stays open on the staged pairs only, so a pair already in the
// cart cannot be removed here.
function deriveCards(products, cartLines, staged) {
  return products.map((product) => {
    const floor = quantityOf(cartLines, (line) => line.productId === product.productId);
    const stagedCount = stagedQuantity(staged, product.productId);
    return {
      product,
      quantity: floor + stagedCount,
      floor,
      minusEnabled: stagedCount > 0,
      plusEnabled: true,
    };
  });
}

function repeat(entry, times) {
  return Array.from({ length: times }, () => ({ ...entry }));
}

/**
 * A grid product built from a cart line, for the added pair before any fetch returns.
 * @param {CartLine} line
 * @param {string} [model]
 * @returns {Product}
 */
export function productFromLine(line, model) {
  return { productId: line.productId, variantId: line.variantId, title: line.title, image: line.image, model };
}

function productForLine(products, line) {
  return products.find((product) => product.productId === line.productId) || productFromLine(line, line.glassesModel);
}

/**
 * Grid products for the pairs already in the cart, the added pair aside. One
 * per product, in cart order, so a pair the customer brought with them is
 * offered a card of its own.
 * @param {Object} input
 * @param {CartLine[]} input.cartLines
 * @param {AddedLine} input.addedLine
 * @returns {Product[]}
 */
export function cartProducts({ cartLines, addedLine }) {
  const seen = new Set([addedLine.productId]);
  return linesInCollection(cartLines, addedLine).flatMap((line) => {
    if (seen.has(line.productId)) return [];
    seen.add(line.productId);
    return [productFromLine(line, line.glassesModel)];
  });
}

/**
 * Empty cells that fill the last row. The slots are shown three to a row, so
 * the row count is rounded up and every row holds three cells.
 * @param {number} filled
 * @returns {number}
 */
export function emptySlotCount(filled) {
  const rows = Math.max(1, Math.ceil(filled / BUNDLE_SIZE));
  return rows * BUNDLE_SIZE - filled;
}

function deriveSlots(lines, products, staged) {
  const cartSlots = lines.flatMap((line) =>
    repeat({ type: 'cart', product: productForLine(products, line), strength: line.strength }, line.quantity)
  );
  const stagedSlots = products.flatMap((product) =>
    stagedPairs(staged, product.productId).map((pair) => ({ type: 'staged', product, strength: pair.strength }))
  );
  const filled = [...cartSlots, ...stagedSlots];
  return [...filled, ...repeat({ type: 'empty' }, emptySlotCount(filled.length))];
}

// One line per variant, so pairs of the same product staged at two strengths
// are added as the two variants the customer chose.
function buildAddPayload(products, staged) {
  return products.flatMap((product) => {
    const byVariant = new Map();
    stagedPairs(staged, product.productId).forEach((pair) => {
      byVariant.set(pair.variantId, (byVariant.get(pair.variantId) || 0) + 1);
    });
    return [...byVariant].map(([id, quantity]) => ({ id, quantity, properties: { _source: SOURCE_PROPERTY } }));
  });
}

/**
 * Derives the whole power step view. Cart lines count at any strength;
 * staged pairs are not in the cart yet and hold the strength they were staged at.
 * @param {Object} input
 * @param {CartLine[]} input.cartLines
 * @param {AddedLine} input.addedLine - The pair just added; its cart line sets the collection
 * @param {Product[]} input.products - Fetched grid products in display order
 * @param {Staged} input.staged
 * @returns {{ cards: Array, count: number, finished: boolean, mode: 'sticky'|'inline', slots: Array, payload: Array }}
 */
export function deriveView({ cartLines, addedLine, products, staged }) {
  const lines = linesInCollection(cartLines, addedLine);
  const count = quantityOf(lines) + Object.values(staged).reduce((total, pairs) => total + pairs.length, 0);
  const finished = count >= BUNDLE_SIZE;

  return {
    cards: deriveCards(products, cartLines, staged),
    count,
    finished,
    mode: finished ? 'inline' : 'sticky',
    slots: deriveSlots(lines, products, staged),
    payload: buildAddPayload(products, staged),
  };
}

/**
 * Builds the grid order from the two fetched sets. The added pair leads, taken
 * from the model set when present so its variant follows the active strength.
 * The pairs already in the cart come next, so every pair counted in the bundle
 * also has a card. The rest is model matches, then shape matches.
 * A failed fetch passes null and counts as empty.
 * @param {Object} input
 * @param {Product} input.added - The added pair, built from cart data
 * @param {Product[]} [input.cartProducts] - Pairs already in the cart, from `cartProducts`
 * @param {Product[]|null} input.modelProducts - Same model, collection order
 * @param {Product[]|null} input.shapeProducts - Same shape, collection order
 * @param {string|null} input.currentModel - Model to drop from the shape set
 * @param {number} [input.limit]
 * @returns {Product[]}
 */
export function mergeProducts({ added, cartProducts: fromCart = [], modelProducts, shapeProducts, currentModel, limit = GRID_SIZE }) {
  const model = modelProducts || [];
  const shape = (shapeProducts || []).filter((product) => product.model !== currentModel);
  const fetched = [...model, ...shape];
  const pick = (product) =>
    fetched.find((entry) => entry.productId === product.productId && entry.available !== false) || product;
  const result = [pick(added)];

  // A cart pair earns its place whether or not the fetches returned it, so it
  // is not held to the availability check the suggestions are.
  for (const product of fromCart) {
    if (result.length >= limit) break;
    if (!result.some((entry) => entry.productId === product.productId)) result.push(pick(product));
  }

  for (const product of fetched) {
    if (result.length >= limit) break;
    const seen = result.some((entry) => entry.productId === product.productId);
    if (!seen && product.available !== false) result.push(product);
  }

  return result;
}

/**
 * Staged pairs that survive a strength change.
 *
 * Declining the cart swap leaves the pairs at the strength and variant they
 * were staged at. Accepting it moves the cart, so the staged pairs move with
 * it: they take the new strength and the variant the grid now holds for them.
 * @param {Staged} staged
 * @param {Product[]} products - Grid products fetched at the new strength
 * @param {string|null} [restageAt] - New strength, when the customer swapped
 * @returns {Staged}
 */
export function keepStagedForProducts(staged, products, restageAt = null) {
  return Object.fromEntries(
    products
      .filter((product) => stagedQuantity(staged, product.productId) > 0)
      .map((product) => [
        product.productId,
        stagedPairs(staged, product.productId).map((pair) =>
          restageAt ? { strength: restageAt, variantId: product.variantId } : pair
        ),
      ])
  );
}

/**
 * Cart lines in the collection that sit at another strength than the target.
 * Empty means a strength change has nothing to swap, so the question is skipped.
 * @param {Object} input
 * @param {CartLine[]} input.cartLines
 * @param {AddedLine} input.addedLine
 * @param {string} input.targetStrength
 * @returns {CartLine[]}
 */
export function pairsToSwap({ cartLines, addedLine, targetStrength }) {
  return linesInCollection(cartLines, addedLine).filter((line) => line.strength !== targetStrength);
}

/**
 * Variant id per product id from grid products fetched at one strength.
 * Out-of-stock variants are left out, so a swap never targets them.
 * @param {Product[]} products
 * @returns {Object<number, number>}
 */
export function variantsByProduct(products) {
  return Object.fromEntries(
    products.filter((product) => product.available !== false).map((product) => [product.productId, product.variantId])
  );
}

/**
 * Strength filter values in numeric order. The collection filter returns them
 * in store order, which puts "+0" last.
 * @param {Array<{ value: string }>} strengths
 * @returns {Array}
 */
export function sortStrengths(strengths) {
  return [...strengths].sort((a, b) => parseFloat(a.value) - parseFloat(b.value));
}

/**
 * The in-stock variant at a strength from the product JSON endpoint.
 * @param {{ options: Array<{ name: string }>, variants: Array }} productJson - Response of /products/<handle>.js
 * @param {string} optionName - Localised strength option name
 * @param {string} strength
 * @returns {number|null}
 */
export function variantAtStrength(productJson, optionName, strength) {
  const index = productJson.options.findIndex((option) => option.name === optionName);
  if (index < 0) return null;
  const variant = productJson.variants.find((entry) => entry.available && entry.options[index] === strength);
  return variant ? variant.id : null;
}

/**
 * Plans the cart calls that move cart lines in the collection to the target strength.
 * Shopify cannot change a line's variant in place, so each line is removed by key
 * and its quantity re-added on the target variant with the same properties.
 * @param {Object} input
 * @param {CartLine[]} input.cartLines
 * @param {AddedLine} input.addedLine
 * @param {string} input.targetStrength
 * @param {Object<number, number>} input.variantByProductId - Variant id at the target strength per product id
 * @param {boolean} input.swap - The customer's yes/no answer
 * @returns {{ changes: Array<{ id: string, quantity: 0 }>, adds: Array, skipped: CartLine[] }|null}
 */
export function planSwap({ cartLines, addedLine, targetStrength, variantByProductId, swap }) {
  if (!swap) return null;

  const lines = pairsToSwap({ cartLines, addedLine, targetStrength });
  const swappable = lines.filter((line) => variantByProductId[line.productId]);
  if (swappable.length === 0) return null;

  return {
    changes: swappable.map((line) => ({ id: line.key, quantity: 0 })),
    adds: swappable.map((line) => ({
      id: variantByProductId[line.productId],
      quantity: line.quantity,
      properties: line.properties || {},
    })),
    skipped: lines.filter((line) => !variantByProductId[line.productId]),
  };
}

/**
 * Decides whether the add-to-cart opens the power step or the cart drawer.
 * @param {Object} input
 * @param {boolean} input.enabled - Theme setting
 * @param {{ glassesModel: string|null, glassesCollection: string|null }} input.product - Added product metafields
 * @param {CartLine[]} input.cartLines - Cart after the add
 * @returns {'power-step'|'drawer'}
 */
export function decideOpen({ enabled, product, cartLines }) {
  if (!enabled || !product.glassesModel || !product.glassesCollection) return 'drawer';

  const pairsInCollection = quantityOf(cartLines, (line) => line.collection === product.glassesCollection);
  return pairsInCollection < BUNDLE_SIZE ? 'power-step' : 'drawer';
}

export const BAR_COVERAGE = 0.8;
export const BAR_TOP_SLACK = 8;

/**
 * Whether the customer sits at the top of the scroller that holds the step.
 * @param {number} scrollTop
 * @returns {boolean}
 */
export function atScrollTop(scrollTop) {
  return scrollTop <= BAR_TOP_SLACK;
}

/**
 * Latches the sticky bar on. The bar waits for the grid so it never covers the
 * confirmation, and once the grid has filled the viewport the bar stays for the
 * rest of the step, also below the grid. Coming back to the top releases the
 * latch, so the top of the step is shown whole again.
 * @param {Object} input
 * @param {boolean} input.latched - The latch so far
 * @param {number} input.coverage - Share of the scroll viewport the bundle section covers, 0 to 1
 * @param {boolean} [input.atTop] - The scroller is back at its top
 * @returns {boolean}
 */
export function latchBar({ latched, coverage, atTop = false }) {
  if (atTop) return false;
  return latched || coverage >= BAR_COVERAGE;
}

/**
 * Whether the bar shows. Review round 3 item 5: the desktop dialog always shows
 * it, so the latch only rules the mobile bar. A finished set shows it inline
 * whatever the latch.
 * @param {Object} input
 * @param {boolean} input.finished
 * @param {boolean} input.latched
 * @param {boolean} [input.desktop]
 * @returns {boolean}
 */
export function barVisible({ finished, latched, desktop = false }) {
  return desktop || finished || latched;
}
